import React, { useState } from 'react';
import { useCompany } from '@/contexts/CompanyContext';
import { Loader2 } from 'lucide-react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

const MODULES = [
    { id: 'crm', name: 'CRM', desc: 'Operations & Pipeline Management', icon: 'avatar', color: '#6366f1' },
    { id: 'analytics', name: 'Analytics', desc: 'Business Intelligence & Reporting', icon: 'bar-chart', color: '#8b5cf6' },
    { id: 'quality', name: 'Quality', desc: 'Audit & QA Governance', icon: 'verified', color: '#10b981' },
    { id: 'lms', name: 'LMS', desc: 'Knowledge & Learning Management', icon: 'academy', color: '#f59e0b' },
    { id: 'automation', name: 'Orbit', desc: 'Automations & Flows', icon: 'bolt', color: '#ec4899' },
    { id: 'ai', name: 'Yoda AI', desc: 'Intelligence & Copilot', icon: 'sparkles', color: '#8b5cf6' },
    { id: 'developer', name: 'Developer', desc: 'Core OS & Integrations', icon: 'code', color: '#10b981' }
];

export default function SystemSettings() {
    const { currentCompany, updateCompany } = useCompany();
    const [updating, setUpdating] = useState(null);

    // If activeModules is undefined, assume all are active
    const activeModules = currentCompany?.activeModules || MODULES.map(m => m.id);

    const handleToggle = async (moduleId) => {
        if (!currentCompany) return;
        setUpdating(moduleId);
        try {
            let newModules = [...activeModules];
            if (newModules.includes(moduleId)) {
                newModules = newModules.filter(id => id !== moduleId);
            } else {
                newModules.push(moduleId);
            }
            await updateCompany(currentCompany.id, { activeModules: newModules });
        } catch (error) {
            console.error('Failed to update module visibility:', error);
        } finally {
            setUpdating(null);
        }
    };

    return (
        <div className="animate-fade-in pb-12">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2 tracking-tight">
                    System Settings
                </h1>
                <p className="text-sm text-[var(--text-secondary)]">
                    Manage which systems and modules are live and accessible in your workspace App Launcher.
                </p>
            </div>

            <div className="bg-[var(--bg-surface)] rounded-2xl border border-[var(--border)] overflow-hidden">
                <div className="p-6 border-b border-[var(--border)]">
                    <h2 className="text-base font-semibold text-[var(--text-primary)]">Modules & Systems</h2>
                    <p className="text-[13px] text-[var(--text-tertiary)] mt-1">
                        Toggle the switch to make a module live or unlive for all users in this workspace.
                    </p>
                </div>
                
                <div className="flex flex-col">
                    {MODULES.map((mod, index) => {
                        const isActive = activeModules.includes(mod.id);
                        const isUpdating = updating === mod.id;
                        
                        return (
                            <div 
                                key={mod.id} 
                                className={`flex items-center justify-between px-6 py-5 transition-colors duration-200 ${
                                    index < MODULES.length - 1 ? 'border-b border-[var(--border-subtle)]' : ''
                                } ${isActive ? 'bg-transparent' : 'bg-[var(--bg-subtle)]'}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div 
                                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                                        style={{ background: isActive ? `${mod.color}15` : 'var(--bg-muted)' }}
                                    >
                                        <ObliqueIcon 
                                            name={mod.icon} 
                                            size={20} 
                                            color={isActive ? mod.color : 'var(--text-tertiary)'} 
                                        />
                                    </div>
                                    <div>
                                        <div className={`text-sm font-semibold flex items-center gap-2 ${
                                            isActive ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'
                                        }`}>
                                            {mod.name}
                                            {isActive && (
                                                <span className="text-[10px] px-1.5 py-0.5 bg-[var(--success-subtle)] text-[var(--success)] rounded uppercase font-bold tracking-wider">Live</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
                                            {mod.desc}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleToggle(mod.id)}
                                    disabled={isUpdating}
                                    className={`relative w-11 h-6 rounded-full border-none transition-colors duration-300 ${
                                        isActive ? 'bg-[var(--accent)]' : 'bg-[var(--bg-strong)]'
                                    } ${isUpdating ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 flex items-center justify-center ${
                                        isActive ? 'left-[22px]' : 'left-0.5'
                                    }`}>
                                        {isUpdating && <Loader2 size={12} className={`animate-spin ${isActive ? 'text-[var(--accent)]' : 'text-gray-400'}`} />}
                                    </div>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
